// ============================================================
// Chess Arena — Preloader Scene
// ============================================================
// Loads all game assets (SVG images, JSON configs) and displays
// a progress bar. Emits 'assets-loaded' via the eventBus when
// loading completes, then optionally launches the next scene.
// ============================================================

import Phaser from 'phaser';
import eventBus from '../core/eventBus';

const BAR_WIDTH = 480;
const BAR_HEIGHT = 36;
const BAR_RADIUS = 18;
const BAR_BG_COLOR = 0x222222;
const BAR_FILL_COLOR = 0x44cc44;
const BAR_BORDER_COLOR = 0x888888;

export class PreloaderScene extends Phaser.Scene {
  private progressBar!: Phaser.GameObjects.Graphics;
  private progressBox!: Phaser.GameObjects.Graphics;
  private loadingText!: Phaser.GameObjects.Text;
  private percentText!: Phaser.GameObjects.Text;

  constructor() {
    super({ key: 'PreloaderScene' });
  }

  // ------------------------------------------------------------------
  // Preload — queue all assets
  // ------------------------------------------------------------------

  preload(): void {
    this.createProgressBar();
    this.registerLoadEvents();
    this.loadPieceSprites();
    this.loadTerrainSprites();
    this.loadUISprites();
    this.loadEffectSprites();
    this.loadTutorialSprites();
    this.loadBattleBackgrounds();
    this.loadMiscSprites();
    this.loadConfigs();
  }

  // ------------------------------------------------------------------
  // Create — called after preload finishes
  // ------------------------------------------------------------------

  create(): void {
    // Brief fade-out of the bar, then signal completion
    this.tweens.add({
      targets: [this.progressBar, this.progressBox, this.loadingText, this.percentText],
      alpha: 0,
      duration: 300,
      onComplete: () => {
        eventBus.emit('assets_loaded', {});
      },
    });
  }

  // ------------------------------------------------------------------
  // Progress bar
  // ------------------------------------------------------------------

  private createProgressBar(): void {
    const { width, height } = this.cameras.main;
    const centerX = width / 2;
    const centerY = height / 2;

    // Background box
    this.progressBox = this.add.graphics();
    this.progressBox.fillStyle(BAR_BG_COLOR, 0.9);
    this.progressBox.fillRoundedRect(
      centerX - BAR_WIDTH / 2 - 4,
      centerY - BAR_HEIGHT / 2 - 4,
      BAR_WIDTH + 8,
      BAR_HEIGHT + 8,
      BAR_RADIUS,
    );
    this.progressBox.lineStyle(2, BAR_BORDER_COLOR, 1);
    this.progressBox.strokeRoundedRect(
      centerX - BAR_WIDTH / 2 - 4,
      centerY - BAR_HEIGHT / 2 - 4,
      BAR_WIDTH + 8,
      BAR_HEIGHT + 8,
      BAR_RADIUS,
    );

    // Fill bar (drawn each frame)
    this.progressBar = this.add.graphics();

    // "Loading..." text
    this.loadingText = this.add.text(centerX, centerY - 60, 'Loading...', {
      fontFamily: 'Fredoka, Arial, sans-serif',
      fontSize: '30px',
      color: '#ffffff',
    }).setOrigin(0.5);

    // Percent text
    this.percentText = this.add.text(centerX, centerY, '0%', {
      fontFamily: 'Fredoka, Arial, sans-serif',
      fontSize: '24px',
      color: '#ffffff',
    }).setOrigin(0.5);
  }

  private registerLoadEvents(): void {
    const { width, height } = this.cameras.main;
    const centerX = width / 2;
    const centerY = height / 2;

    this.load.on('progress', (value: number) => {
      this.progressBar.clear();
      this.progressBar.fillStyle(BAR_FILL_COLOR, 1);
      this.progressBar.fillRoundedRect(
        centerX - BAR_WIDTH / 2,
        centerY - BAR_HEIGHT / 2,
        BAR_WIDTH * value,
        BAR_HEIGHT,
        BAR_RADIUS,
      );
      this.percentText.setText(`${Math.round(value * 100)}%`);
    });

    this.load.on('complete', () => {
      this.loadingText.setText('Ready!');
    });
  }

  // ------------------------------------------------------------------
  // Asset loading helpers
  // ------------------------------------------------------------------

  private loadPieceSprites(): void {
    const pieceTypes = ['king', 'pawn', 'knight', 'bishop', 'rook', 'queen'];
    const teams = ['blue', 'red'];

    for (const type of pieceTypes) {
      for (const team of teams) {
        const key = `piece_${type}_${team}`;
        this.load.image(key, `assets/pieces/${key}.png`);
      }
    }

    // Ghost piece overlay
    this.load.svg('piece_ghost', 'assets/pieces/piece_ghost.svg', { width: 128, height: 128 });

    // Cooldown clock overlay
    this.load.svg('icon_clock', 'assets/pieces/overlay_cooldown_clock.svg', { width: 64, height: 64 });

    // Premove arrow overlay
    this.load.svg('overlay_premove_arrow', 'assets/pieces/overlay_premove_arrow.svg', { width: 64, height: 64 });
  }

  private loadTerrainSprites(): void {
    this.load.image('terrain_rock', 'assets/terrain/terrain_rock.png');
    this.load.image('terrain_rock_variant_1', 'assets/terrain/terrain_rock_variant_1.png');
    this.load.image('terrain_bush', 'assets/terrain/terrain_bush.png');
    // bush_hidden still uses SVG until we generate a PNG replacement
    this.load.svg('terrain_bush_hidden', 'assets/terrain/terrain_bush_hidden.svg', { width: 128, height: 128 });
  }

  private loadUISprites(): void {
    const svgSize = { width: 64, height: 64 };

    this.load.image('icon_coin', 'assets/ui/icon_coin.png');
    this.load.image('icon_trophy', 'assets/ui/icon_trophy.png');
    this.load.image('icon_heart', 'assets/ui/icon_heart.png');
    this.load.image('icon_xp_star', 'assets/ui/icon_xp_star.png');
    this.load.svg('icon_lock', 'assets/ui/icon_lock.svg', svgSize);

    // Chest icons
    this.load.image('icon_chest_bronze', 'assets/ui/icon_chest_bronze.png');
    this.load.image('icon_chest_silver', 'assets/ui/icon_chest_silver.png');
    this.load.image('icon_chest_gold', 'assets/ui/icon_chest_gold.png');
    this.load.image('icon_chest_royal', 'assets/ui/icon_chest_royal.png');

    // Ability icons
    this.load.svg('icon_ability_reroll', 'assets/ui/icon_ability_reroll.svg', svgSize);
    this.load.svg('icon_ability_instant_spawn', 'assets/ui/icon_ability_instant_spawn.svg', svgSize);
    this.load.svg('icon_ability_bomb', 'assets/ui/icon_ability_bomb.svg', svgSize);
    this.load.svg('icon_ability_freeze', 'assets/ui/icon_ability_freeze.svg', svgSize);
    this.load.svg('icon_ability_shield', 'assets/ui/icon_ability_shield.svg', svgSize);
    this.load.svg('icon_ability_lightning', 'assets/ui/icon_ability_lightning.svg', svgSize);
  }

  private loadEffectSprites(): void {
    const svgSize = { width: 128, height: 128 };

    this.load.svg('fx_spawn_glow', 'assets/effects/fx_spawn_glow.svg', svgSize);
    this.load.svg('fx_capture_flash', 'assets/effects/fx_capture_flash.svg', svgSize);
    this.load.svg('fx_coin_fly', 'assets/effects/fx_coin_fly.svg', { width: 32, height: 32 });
  }

  private loadTutorialSprites(): void {
    this.load.svg('tutorial_hand_pointer', 'assets/tutorial/tutorial_hand_pointer.svg', { width: 96, height: 96 });
    this.load.image('tutorial_mascot_king', 'assets/tutorial/mascot_king.png');
  }

  private loadBattleBackgrounds(): void {
    this.load.image('bg_battle_arena', 'assets/battle/bg_battle_arena.png');
    this.load.image('hud_bottom_tray', 'assets/battle/hud_bottom_tray.png');
  }

  private loadMiscSprites(): void {
    this.load.svg('logo_chess_arena', 'assets/logo_chess_arena.svg', { width: 256, height: 128 });
  }

  private loadConfigs(): void {
    this.load.json('gameSettings', 'config/GameSettings.json');
    this.load.json('progression', 'config/Progression.json');
    this.load.json('spawnDefaults', 'config/SpawnDefaults.json');
    this.load.json('teamColors', 'config/TeamColors.json');
    this.load.json('uiText', 'config/UIText.json');
    this.load.json('proceduralConfig', 'config/ProceduralConfig.json');
    this.load.json('opponentPool', 'config/OpponentPool.json');
    this.load.json('dailyTasks', 'config/DailyTasks.json');
    this.load.json('chestConfig', 'config/ChestConfig.json');
    this.load.json('tutorialConfig', 'config/TutorialConfig.json');
    this.load.json('assetManifest', 'config/AssetManifest.json');
    this.load.json('ToolConfig', 'config/ToolConfig.json');
    this.load.json('AbilityConfig', 'config/AbilityConfig.json');

    // Load hand-tuned level configs (Level01–Level12)
    for (let i = 1; i <= 12; i++) {
      const key = `Level${String(i).padStart(2, '0')}`;
      this.load.json(key, `config/Levels/${key}.json`);
    }
  }
}
